import type { jsPDF as JsPDFType } from 'jspdf';
import { INTENSITY_LABELS } from './constants/painConstants';

interface PainArea {
  area: string;
  intensity: 1 | 2 | 3;
}

interface EDCheckin {
  id: string;
  timestamp: string;
  visitId: string;
  painLevel: number;
  vocStatus: string;
  bodyAreas: PainArea[];
  safetyFlags: string[];
}

interface GenerateEDReportPDFOptions {
  edScript: string;
  edCheckins: EDCheckin[];
  edReportNote: string;
  bodyAreaNames: Record<string, string>;
}

function loadImageAsDataURL(url: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth || img.width;
      canvas.height = img.naturalHeight || img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('Could not get canvas context'));
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
    img.src = url;
  });
}

function wrapText(doc: JsPDFType, text: string, x: number, y: number, maxWidth: number, lineHeight: number): number {
  const lines = doc.splitTextToSize(text, maxWidth);
  doc.text(lines, x, y);
  return y + lines.length * lineHeight;
}

export async function generateEDReportPDF(opts: GenerateEDReportPDFOptions): Promise<void> {
  const { edScript, edCheckins, edReportNote, bodyAreaNames } = opts;

  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ unit: 'pt', format: 'letter', orientation: 'portrait' });

  const PAGE_W = doc.internal.pageSize.getWidth();
  const PAGE_H = doc.internal.pageSize.getHeight();
  const MARGIN = 48;
  const CONTENT_W = PAGE_W - MARGIN * 2;
  const LINE_H = 14;

  let y = MARGIN;

  const BASE_URL = import.meta.env.BASE_URL ?? '/';
  const careweaveLogoUrl = `${BASE_URL}careweave-logo-v9-woven-net.svg`;
  const sickKidsLogoUrl = `${BASE_URL}sickkids-logo.png`;

  let careweaveDataUrl: string | null = null;
  let sickKidsDataUrl: string | null = null;

  try {
    careweaveDataUrl = await loadImageAsDataURL(careweaveLogoUrl);
  } catch {
    careweaveDataUrl = null;
  }
  try {
    sickKidsDataUrl = await loadImageAsDataURL(sickKidsLogoUrl);
  } catch {
    sickKidsDataUrl = null;
  }

  const HEADER_H = 64;
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, PAGE_W, HEADER_H, 'F');
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.5);
  doc.line(MARGIN, HEADER_H - 1, PAGE_W - MARGIN, HEADER_H - 1);

  const LOGO_H = 40;
  if (careweaveDataUrl) {
    doc.addImage(careweaveDataUrl, 'PNG', MARGIN, (HEADER_H - LOGO_H) / 2, 120, LOGO_H);
  } else {
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(99, 102, 241);
    doc.text('CareWeave', MARGIN, HEADER_H / 2 + 6);
  }

  if (sickKidsDataUrl) {
    doc.addImage(sickKidsDataUrl, 'PNG', PAGE_W - MARGIN - 120, (HEADER_H - LOGO_H) / 2, 120, LOGO_H);
  } else {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 61, 165);
    doc.text('SickKids', PAGE_W - MARGIN - 60, HEADER_H / 2 + 5, { align: 'center' });
  }

  y = HEADER_H + 24;

  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(26, 26, 46);
  doc.text('Crisis Visit Report', MARGIN, y);
  y += LINE_H * 1.4;

  const now = new Date();
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(120, 120, 140);
  doc.text(`Generated ${now.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })} at ${now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`, MARGIN, y);
  y += LINE_H * 2;

  function sectionHeading(title: string): void {
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(99, 102, 241);
    doc.text(title.toUpperCase(), MARGIN, y);
    y += 4;
    doc.setDrawColor(99, 102, 241);
    doc.setLineWidth(0.75);
    doc.line(MARGIN, y, PAGE_W - MARGIN, y);
    y += LINE_H;
  }

  function bodyText(text: string): void {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(40, 40, 60);
    y = wrapText(doc, text, MARGIN, y, CONTENT_W, LINE_H);
    y += 4;
  }

  function ensureSpace(needed: number): void {
    if (y + needed > PAGE_H - MARGIN) {
      doc.addPage();
      y = MARGIN + 16;
    }
  }

  sectionHeading('Advocacy Script');
  ensureSpace(LINE_H * 4);
  if (edScript && edScript.trim()) {
    bodyText(edScript);
  } else {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(140, 140, 160);
    doc.text('No script saved yet.', MARGIN, y);
    y += LINE_H;
  }
  y += 10;

  ensureSpace(LINE_H * 4);
  sectionHeading("Today's Crisis Snapshots");

  if (edCheckins.length === 0) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(140, 140, 160);
    doc.text('No check-ins logged during this visit.', MARGIN, y);
    y += LINE_H;
  } else {
    edCheckins.forEach((c, i) => {
      ensureSpace(LINE_H * 7);
      const t = new Date(c.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

      doc.setFillColor(250, 245, 245);
      doc.setDrawColor(239, 68, 68);
      doc.setLineWidth(0.5);
      const boxStartY = y - LINE_H + 2;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(26, 26, 46);
      doc.text(`Check-in ${i + 1}  —  ${t}`, MARGIN + 8, y);
      y += LINE_H;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(80, 80, 100);
      doc.text(`VOC Status: `, MARGIN + 8, y);
      doc.setTextColor(26, 26, 46);
      doc.text(c.vocStatus || 'Not specified', MARGIN + 8 + 58, y);
      y += LINE_H;

      doc.setTextColor(80, 80, 100);
      doc.text(`Pain Level: `, MARGIN + 8, y);
      doc.setTextColor(239, 68, 68);
      doc.setFont('helvetica', 'bold');
      doc.text(`${c.painLevel}/10`, MARGIN + 8 + 54, y);
      doc.setFont('helvetica', 'normal');
      y += LINE_H;

      if (c.bodyAreas.length > 0) {
        doc.setTextColor(80, 80, 100);
        doc.text('Areas: ', MARGIN + 8, y);
        const areasText = c.bodyAreas.map(a => `${bodyAreaNames[a.area] || a.area} (${INTENSITY_LABELS[a.intensity]})`).join(', ');
        doc.setTextColor(26, 26, 46);
        y = wrapText(doc, areasText, MARGIN + 8 + 36, y, CONTENT_W - 44, LINE_H);
        y += 2;
      }

      if (c.safetyFlags.length > 0) {
        doc.setTextColor(180, 80, 0);
        doc.setFont('helvetica', 'bold');
        doc.text(`⚠ Flags: ${c.safetyFlags.join(', ')}`, MARGIN + 8, y);
        doc.setFont('helvetica', 'normal');
        y += LINE_H;
      }

      const boxEndY = y + 4;
      doc.setFillColor(255, 250, 250);
      doc.setDrawColor(239, 68, 68);
      doc.setLineWidth(0.5);
      doc.roundedRect(MARGIN, boxStartY, CONTENT_W, boxEndY - boxStartY, 4, 4, 'FD');
      doc.setDrawColor(239, 68, 68);
      doc.setLineWidth(2);
      doc.line(MARGIN, boxStartY, MARGIN, boxEndY);

      y = boxEndY + 10;
    });
  }

  y += 6;
  ensureSpace(LINE_H * 4);
  sectionHeading('Pattern Highlights');
  bodyText('Recent stigma scores: 8.2/10 average during crisis visits');
  bodyText('Common tags: Drug-seeking, Security called, Race/Racism');
  y += 6;

  if (edReportNote && edReportNote.trim()) {
    ensureSpace(LINE_H * 5);
    sectionHeading('What I Want This Team to Know');
    bodyText(edReportNote);
    y += 6;
  }

  const totalPages = doc.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(160, 160, 180);
    doc.text('Confidential — for medical staff use', MARGIN, PAGE_H - 24);
    doc.text(`Page ${p} of ${totalPages}`, PAGE_W - MARGIN, PAGE_H - 24, { align: 'right' });
  }

  const dateStr = now.toISOString().slice(0, 10);
  doc.save(`ed-report-${dateStr}.pdf`);
}
